import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import { useTheme } from 'styled-components';
import ActionIcon from '../ActionIcon';
import ModalOverlay from '../ModalOverlay';
import { tokens, inkA } from '../tokens';
import useIsMobile from '../useIsMobile';

// Phone bottom sheet (z 1100). Below 768px it rises from the bottom edge
// with a grab handle, a title / subtitle head, a scrolling body and a
// footer. At 768px and up the same content shows as a centred dialog
// (max 440px wide), so one component serves both layouts. Esc, a backdrop
// tap and the optional close button all call onClose.

let nextId = 0;

const BottomSheet = ({
  open,
  onClose,
  title,
  subtitle,
  label,
  children,
  footer,
  zIndex,
  showClose,
  maxHeight,
  initialFocusRef,
}) => {
  const theme = useTheme();
  const t = tokens(theme);
  const isMobile = useIsMobile();
  const idRef = useRef(null);
  if (!idRef.current) {
    nextId += 1;
    idRef.current = `v3-sheet-${nextId}`;
  }
  const titleId = `${idRef.current}-title`;

  const panelStyle = isMobile
    ? {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxHeight,
        background: t.card,
        color: t.text,
        borderTop: '1px solid ' + t.border,
        borderRadius: '18px 18px 0 0',
        boxShadow: '0 -18px 48px rgba(0, 0, 0, 0.5)',
        padding: '8px 12px calc(12px + env(safe-area-inset-bottom, 0px))',
      }
    : {
        display: 'flex',
        flexDirection: 'column',
        width: 'calc(100vw - 32px)',
        maxWidth: 440,
        maxHeight: 'calc(100vh - 48px)',
        background: t.card,
        color: t.text,
        border: '1px solid ' + t.border,
        borderRadius: 16,
        boxShadow: t.shadowPop,
        padding: '18px 16px 16px',
      };

  return (
    <ModalOverlay
      open={open}
      onClose={onClose}
      labelledBy={title ? titleId : undefined}
      label={title ? undefined : label}
      placement={isMobile ? 'bottom' : 'center'}
      zIndex={zIndex}
      initialFocusRef={initialFocusRef}
      panelStyle={panelStyle}
    >
      {isMobile ? (
        <div
          aria-hidden="true"
          style={{
            width: 40,
            height: 4,
            borderRadius: 2,
            background: inkA(theme, 0.22),
            margin: '2px auto 10px',
            flexShrink: 0,
          }}
        />
      ) : null}
      {title || subtitle || showClose ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '0 4px 12px 6px',
            flexShrink: 0,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            {title ? (
              <h2
                id={titleId}
                style={{
                  margin: 0,
                  fontSize: 16,
                  fontWeight: 700,
                  lineHeight: 1.3,
                  color: t.text,
                  overflowWrap: 'anywhere',
                }}
              >
                {title}
              </h2>
            ) : null}
            {subtitle ? (
              <div
                style={{
                  marginTop: 3,
                  fontSize: 12.5,
                  lineHeight: 1.45,
                  color: t.sub,
                }}
              >
                {subtitle}
              </div>
            ) : null}
          </div>
          {showClose ? (
            <ActionIcon icon="x" title="Close" onClick={onClose} />
          ) : null}
        </div>
      ) : null}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>{children}</div>
      {footer ? (
        <div style={{ flexShrink: 0, paddingTop: 10 }}>{footer}</div>
      ) : null}
    </ModalOverlay>
  );
};

BottomSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  label: PropTypes.string,
  children: PropTypes.node,
  footer: PropTypes.node,
  zIndex: PropTypes.number,
  showClose: PropTypes.bool,
  maxHeight: PropTypes.string,
  initialFocusRef: PropTypes.shape({ current: PropTypes.any }),
};

BottomSheet.defaultProps = {
  title: null,
  subtitle: null,
  label: 'Sheet',
  children: null,
  footer: null,
  zIndex: 1100,
  showClose: true,
  maxHeight: '88vh',
  initialFocusRef: undefined,
};

export default BottomSheet;
